import { defineMessages, MessageDescriptor } from '@redbubble/next/intl';
import { addDDError } from '../../pages/cart/utils/datadog';
import { ObjectValues } from '../../types';
import { ErrorMessage, PayError } from './types';

export class PaypalPaymentValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PaypalPaymentValidationError';
  }
}

export const intlErrorMessages = defineMessages({
  initFailed: {
    defaultMessage:
      'Sorry, something went wrong. Please try again or select another payment method.',
  },
  unprocessablePayment: {
    defaultMessage:
      'We were unable to process your payment. Please try another payment method.',
  },
  serverError: {
    defaultMessage:
      'We were unable to process your payment. Please contact customer support.',
  },
  retryablePayment: {
    defaultMessage: 'We were unable to process your payment. Please try again.',
  },
});

// XXX(ai): These are duplicated in app/types/monolith/PaymentErrorCodes.ts.
// These files will be merged at some point to remove the duplication.
// See the above file for details about these errors.
export enum ErrorCodes {
  INIT_FAILED = 'init_failed',
  CHARGE_FAILED = 'charge_failed',
  CHARGE_FAILED_RETRY_ALLOWED = 'charge_failed_retry_allowed',
  INVALID_AMOUNT = 'invalid_amount',
  INVALID_COUPON = 'invalid_coupon',
  FRAUD_DECLINED = 'fraud_declined',
  SERVER_ERROR = 'server_error',
  VALIDATION_ERROR = 'validation_error',
  SHIPPING_COUNTRY_BLOCKED = 'shipping_country_blocked',
  LICENSED_WORK_NOT_SALEABLE = 'licensed_work_not_saleable',
  NO_PURCHASABLE_ITEMS = 'no_purchasable_items_error',
  DEFAULT = 'default',
  INVALID_ADDRESS = 'invalid_address',
}

const errorDict = (code: string, fallbackMessage?: string) => {
  const defaultMessage = fallbackMessage || '';
  const errorDictMap: Record<
    ObjectValues<typeof ErrorCodes>,
    MessageDescriptor | string | null
  > = {
    [ErrorCodes.INIT_FAILED]: intlErrorMessages.initFailed,
    [ErrorCodes.CHARGE_FAILED]: intlErrorMessages.unprocessablePayment,
    [ErrorCodes.CHARGE_FAILED_RETRY_ALLOWED]:
      intlErrorMessages.retryablePayment,
    [ErrorCodes.INVALID_AMOUNT]: defaultMessage,
    [ErrorCodes.INVALID_COUPON]: defaultMessage,
    [ErrorCodes.FRAUD_DECLINED]: intlErrorMessages.unprocessablePayment,
    [ErrorCodes.SERVER_ERROR]: intlErrorMessages.serverError,
    [ErrorCodes.VALIDATION_ERROR]: defaultMessage,
    [ErrorCodes.SHIPPING_COUNTRY_BLOCKED]: defaultMessage,
    [ErrorCodes.DEFAULT]: defaultMessage,
    [ErrorCodes.LICENSED_WORK_NOT_SALEABLE]: defaultMessage,
    [ErrorCodes.NO_PURCHASABLE_ITEMS]: defaultMessage,
    [ErrorCodes.INVALID_ADDRESS]: defaultMessage,
  };
  return errorDictMap[code];
};

export const getPaymentErrorMessage = ({
  code,
  title,
}: PayError): ErrorMessage => {
  const knownError = errorDict(code, title) as ErrorMessage;

  if (knownError) return knownError;

  addDDError(
    new PaypalPaymentValidationError(
      `PayPal returned unknown error code "${code}"`
    )
  );

  return intlErrorMessages.unprocessablePayment as ErrorMessage;
};
